import { Platform, Pressable, View } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Svg, { Path } from 'react-native-svg';

// Matches the painted corner cuts: 24px top-right, 20px bottom-left.
// Exported so embeds that fill the whole card (e.g. chart iframes) can crop
// their content to the same silhouette.
export const CORNER_CLIP = 'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 20px 100%, 0 calc(100% - 20px))';

const splitShadowLayers = (value) => {
    // Split comma-separated layers, ignoring commas inside rgba(...) parens.
    const layers = [];
    let depth = 0;
    let current = '';
    for (const char of value) {
        if (char === '(') depth += 1;
        if (char === ')') depth -= 1;
        if (char === ',' && depth === 0) {
            layers.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    layers.push(current);
    return layers;
};

const tokenizeShadowLayer = (layer) => {
    // Split on whitespace while keeping fn(...) groups (colors) intact.
    const tokens = [];
    let depth = 0;
    let current = '';
    for (const char of layer) {
        if (char === '(') depth += 1;
        if (char === ')') depth -= 1;
        if (/\s/.test(char) && depth === 0) {
            if (current) {
                tokens.push(current);
                current = '';
            }
        } else {
            current += char;
        }
    }
    if (current) tokens.push(current);
    return tokens;
};

// box-shadow draws around the element's full rectangle, which would outline
// the fake cut corners on web. Converting each layer to a drop-shadow()
// filter lets the browser trace the clip-path silhouette instead. Returns
// null when the value can't be converted so callers keep the mask rendering.
const boxShadowToDropShadow = (value) => {
    if (typeof value !== 'string') return null;
    const source = value.trim();
    if (!source || source === 'none') return null;

    const filters = [];
    for (const layer of splitShadowLayers(source)) {
        const tokens = tokenizeShadowLayer(layer.trim());
        if (!tokens.length) return null;
        // inset layers paint inside the box; drop-shadow has no equivalent.
        if (tokens.includes('inset')) continue;

        const isLength = (token) => /^-?\d*\.?\d+(px|em|rem|vw|vh)$/i.test(token) || token === '0';
        const lengths = tokens.filter(isLength);
        const colorTokens = tokens.filter((token) => !isLength(token));
        if (lengths.length < 2 || lengths.length > 4 || colorTokens.length !== 1) return null;
        // drop-shadow() takes x y blur color — the optional 4th box-shadow
        // spread length is dropped since drop-shadow doesn't support it.
        const [x, y, blur = '0px'] = lengths;
        filters.push(`drop-shadow(${x} ${y} ${blur} ${colorTokens[0]})`);
    }
    return filters.length ? filters.join(' ') : null;
};

// Unistyles defines style props as non-enumerable secrets carriers, so
// StyleSheet.flatten can't see caller values — resolve them by name across
// the (possibly nested) style array, later entries winning.
const readStyleValue = (style, prop) => {
    if (!style || typeof style !== 'object') return undefined;
    if (Array.isArray(style)) {
        return style.reduce((value, entry) => readStyleValue(entry, prop) ?? value, undefined);
    }
    return style[prop];
};

const CutCard = ({ children, accent = true, accentColor, style, contentStyle, testID, onPress, disabled = false, accessibilityLabel }) => {
    const { theme: { colors, shadows, dark } } = useUnistyles();
    const lineColor = accentColor ? colors[accentColor] || accentColor : colors.surfaceVariant;

    // Web only: when the caller painted a box-shadow on the card, swap the
    // SVG mask trick for a real clip-path fill + drop-shadow filter so the
    // shadow hugs the cut corners instead of the element's rectangle.
    const dropShadow = Platform.OS === 'web' && accent ? boxShadowToDropShadow(readStyleValue(style, 'boxShadow')) : null;
    const clipped = !!dropShadow;
    const hoverDropShadow = clipped ? boxShadowToDropShadow(shadows.cardHover) : null;
    const hoverFilter = clipped ? `brightness(${dark ? 1.18 : 0.97})${hoverDropShadow ? ` ${hoverDropShadow}` : ''}` : null;
    const backgroundOverride = readStyleValue(style, 'backgroundColor');
    const fillColor = backgroundOverride && backgroundOverride !== 'transparent' ? backgroundOverride : colors.surface;

    const corners = accent && (
        <>
            <View pointerEvents="none" style={styles.topCorner}>
                <Svg width={24} height={24}>
                    {!clipped && <Path d="M0 0H24V24Z" fill={colors.background} />}
                    <Path d="M0 0L24 24" stroke={lineColor} strokeWidth={1} />
                </Svg>
            </View>
            {!clipped && (
                <View pointerEvents="none" style={styles.bottomCorner}>
                    <Svg width={20} height={20}>
                        <Path d="M0 0V20H20Z" fill={colors.background} />
                    </Svg>
                </View>
            )}
        </>
    );
    const content = <View style={contentStyle}>{children}</View>;
    const cardStyle = (pressed) => [
        styles.card,
        onPress && styles.interactiveCard(hoverFilter),
        style,
        clipped && styles.clippedCard(dropShadow),
        disabled && styles.disabled,
        pressed && styles.pressed,
    ];
    const inner = (
        <>
            {clipped && <View pointerEvents="none" style={styles.clippedFill(fillColor)} />}
            {corners}
            {content}
        </>
    );

    if (!onPress) {
        return (
            <View testID={testID} style={cardStyle(false)}>
                {inner}
            </View>
        );
    }

    return (
        <Pressable
            testID={testID}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            disabled={disabled}
            android_ripple={{ color: colors.hoverStrong }}
            onPress={onPress}
            style={({ pressed }) => cardStyle(pressed)}
        >
            {inner}
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, dark }) => ({
    card: {
        backgroundColor: colors.surface,
        borderRadius: 3,
        overflow: 'hidden',
    },
    clippedCard: (filter) => ({
        backgroundColor: 'transparent',
        boxShadow: 'none',
        _web: { filter },
    }),
    clippedFill: (backgroundColor) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 3,
        backgroundColor,
        _web: { clipPath: CORNER_CLIP },
    }),
    interactiveCard: (hoverFilter) => ({
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.16s ease, transform 0.16s ease, opacity 0.16s ease, filter 0.16s ease',
            _hover: hoverFilter
                ? { filter: hoverFilter, transform: [{ translateY: -1 }] }
                : { backgroundColor: colors.hoverCard, filter: `brightness(${dark ? 1.12 : 0.97})`, transform: [{ translateY: -1 }] },
            _active: {
                transform: [{ translateY: 0 }],
            },
        },
    }),
    pressed: {
        opacity: 0.92,
    },
    disabled: {
        opacity: 0.55,
    },
    topCorner: { position: 'absolute', top: 0, right: 0, zIndex: 1 },
    bottomCorner: { position: 'absolute', bottom: 0, left: 0, zIndex: 1 },
}));

export default CutCard;
